/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片筛选状态管理 Slice
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-12-19 16:15:53
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../app/store'
import dayjs, { Dayjs } from 'dayjs'
import '@/utils/dayjs' // 确保 dayjs 插件已加载
import { DepartmentMemberV2 } from '../global'

// 部门类型定义（从 global.d.ts 复制）
interface DepartmentV2 {
  id: string
  name: string
  num: number
  children?: Array<DepartmentV2>
  users?: Array<DepartmentMemberV2>
}

// 推荐搜索词类型
export interface SuggestedSearchWord {
  word: string
  isChecked: boolean
}

// 筛选条件类型
export interface PhotoFilterState {
  searchKeywords: string[]
  /** 排除关键词：命中这些词的照片要剔除（对齐 v5 dto.Filter.ExcludeKeywords）。
   *  AI 照片君 apply_page_filter 推"含 X 但排除 Y"时带过来。之前漏声明此字段，
   *  dispatch 后被丢弃，主页查询的 excludeKeywords 一直为空，导致排除没生效。 */
  excludeKeywords?: string[]
  suggestedSearchWords: SuggestedSearchWord[]
  mediaType: number | null // 0-照片，1-视频，null-全部
  sourceType: number | null
  times: [Dayjs, Dayjs] | null
  departmentUsers: DepartmentMemberV2[]
  departments: DepartmentV2[]
  watermarks: string[]
  locations?: Array<{ name: string; detail: string; type: number }> // 地点筛选
  useSynonym: boolean // 是否使用相近词（已废弃，但保留字段）
  useSegment: boolean // 是否使用分词
  useSuggestedSearch: boolean // 是否使用推荐搜索
  existWatermark?: number // 0-不限，1-含水印，2-无水印（照片台账表用）
  existPhotoComment?: string // '0'-全部，'1'-无评论，'2'-有评论（照片台账表用）
  isNlpFilter?: boolean // 是否为自然语言筛选
  /** NLP 自然语言原始查询语句（聊天 chip 显示 + AI 照片王 nlQuery 字段） */
  nlQuery?: string
  /** 地点推荐筛选（AI 照片王 PhotosList 透传给后端 LocationRecommend 字段） */
  locationRecommend?: { word: string; key: string } | null
  /** 按人员维度聚合展示（与 FilterState.groupByUser 一致） */
  groupByUser: boolean
  /** 照片标签筛选 */
  labelIDs?: string[]
  labelMatchAll?: boolean
}

// 照片筛选 Slice 状态
interface PhotoFilterSliceState {
  uiFilter: PhotoFilterState
  searchFilter: PhotoFilterState | null
  dates: [Dayjs, Dayjs] | null
  lastPageCond: string
  isAscSort: boolean
  searchTrackingId: string
}

// 初始筛选条件
const initialFilter: PhotoFilterState = {
  searchKeywords: [],
  excludeKeywords: [],
  suggestedSearchWords: [],
  mediaType: null,
  sourceType: null,
  times: [dayjs().subtract(6, 'months').startOf('day'), dayjs().endOf('day')],
  departmentUsers: [],
  departments: [],
  watermarks: [],
  locations: [],
  useSynonym: true,
  useSegment: true,
  useSuggestedSearch: true,
  existWatermark: 0,
  isNlpFilter: false,
  groupByUser: false
}

// 初始状态
const initialState: PhotoFilterSliceState = {
  uiFilter: initialFilter,
  searchFilter: null,
  dates: initialFilter.times,
  lastPageCond: '',
  isAscSort: false,
  searchTrackingId: ''
}

const photoFilterSlice = createSlice({
  name: 'photoFilter',
  initialState,
  reducers: {
    // 更新页面 UI 状态
    setUiFilter: (state, action: PayloadAction<Partial<PhotoFilterState>>) => {
      state.uiFilter = {
        ...state.uiFilter,
        ...action.payload
      }
    },
    // 筛选条件
    setSearchFilter: (state, action: PayloadAction<PhotoFilterState>) => {
      state.searchFilter = action.payload
    },
    // 重置筛选条件
    resetFilter: (state) => {
      state.uiFilter = { ...initialFilter }
      state.searchFilter = { ...initialFilter }
    },
    // 设置日期范围
    setDates: (state, action: PayloadAction<[Dayjs, Dayjs] | null>) => {
      state.dates = action.payload
      if (action.payload) {
        state.uiFilter.times = action.payload
      }
    },
    // 设置分页条件
    setLastPageCond: (state, action: PayloadAction<string>) => {
      state.lastPageCond = action.payload
    },
    // 设置排序方向
    setIsAscSort: (state, action: PayloadAction<boolean>) => {
      state.isAscSort = action.payload
    },
    // 设置 searchTrackingId
    setSearchTrackingId: (state, action: PayloadAction<string>) => {
      state.searchTrackingId = action.payload
      // 同步到 searchFilter
      if (state.searchFilter) {
        state.searchFilter = {
          ...state.searchFilter,
          searchTrackingId: action.payload
        } as any
      }
    },
    // 设置推荐搜索词
    setSuggestedSearchWords: (
      state,
      action: PayloadAction<SuggestedSearchWord[]>
    ) => {
      state.uiFilter.suggestedSearchWords = action.payload
    }
  }
})

export const {
  setUiFilter,
  setSearchFilter,
  resetFilter,
  setDates,
  setLastPageCond,
  setIsAscSort,
  setSuggestedSearchWords,
  setSearchTrackingId
} = photoFilterSlice.actions

export const selectUiFilter = (state: RootState) => state.photoFilter?.uiFilter
export const selectSearchFilter = (state: RootState) =>
  state.photoFilter?.searchFilter
export const selectDates = (state: RootState) => state.photoFilter?.dates
export const selectLastPageCond = (state: RootState) =>
  state.photoFilter?.lastPageCond
export const selectIsAscSort = (state: RootState) =>
  state.photoFilter?.isAscSort
export const selectSearchTrackingId = (state: RootState) =>
  state.photoFilter?.searchTrackingId || ''

export default photoFilterSlice.reducer
